import React, { useEffect, useRef, useState } from 'react';
import { RGBColor } from 'react-color';
import { useIdleTimer } from "react-idle-timer";
import { useNavigate } from 'react-router-dom';
import { APIGetUserPreferencesByUserUuid, IUserPreferences, IUserPreferencesResponse } from '../../../api/APIUserPreferences';
import { APIGetUserSettings } from '../../../api/APIUserSettings';
import { APIGetGeoLocation, APIGetWeatherData } from '../../../api/APIWeather';
import { useAppDispatch, useAppSelector } from '../../../AppHooks';
import _settings, { _settings_customer, _settings_non_private_routes } from '../../../AppSettings';
import { setHardinessZone, setLocation, setLoggedIn, setTemperatureUnit, setUserPreferences as setUserSlicePreferences } from '../../../slices/UserSlice';
import { setCurrentTemp, setWeatherData } from '../../../slices/WeatherSlice';
import { IUserSettings } from '../../../views/Dashboard';
import { returnHasClearanceForFeature, rgbaColorToRgbaString } from '../Common';
import { AppFeatureType, AppSubFeatureType } from '../privateRoute/SecurityClearance';
import { MenuButton } from './Button';

interface IMenuProps {
  	isMenuOpen: boolean;
	setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const LeftMenu = ({ isMenuOpen, setIsMenuOpen }: IMenuProps) => {
	const navigate = useNavigate();
	// Load user database values for theme color
	const [menuThemeColor, setMenuThemeColor] = useState<RGBColor | null>(null);
	const [userPreferences, setUserPreferences] = useState<IUserPreferences>(null);
	const [isAPIDown, setIsAPIDown] = useState(false);
	const isLoggedIn = useAppSelector(state => state.users.isLoggedIn);
	const dispatch = useAppDispatch();
	const [hasClearanceForAdmin, setHasClearanceForAdmin] = useState(false);
	const [isV2PagesContextMenuOpen, setIsV2PagesContextMenuOpen] = useState(false);
	const contextMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // TODO: Check if user has clearance for this feature.
        // Check what plan the user has and if they have clearance for the feature
		// console.log('Checking clearance for Admin [LeftMenu]...');
		        
        const checkClearance = async () => {
            await returnHasClearanceForFeature(AppFeatureType.Admin, AppSubFeatureType.None).then((hasClearance) => {                
				// console.log('Has clearance for Admin [LeftMenu]: ', hasClearance);
				
                setHasClearanceForAdmin(hasClearance);
            });
        }
        checkClearance();
    }, [isLoggedIn]);

	// Load user theme info on initial load and whenever user is logged in
	useEffect(() => {
		getUserPrefs();

		// Check if we should allow mobile users on the app
		const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		// console.log('Is mobile: ', isMobile);
		
		if (isMobile && !_settings.ENABLE_MOBILE) {
			alert("Mobile devices are not supported at this time. Please use a computer.");
			navigate('/about');
		}
	}, [])

	// Handle click-away for context menu
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
				setIsV2PagesContextMenuOpen(false);
			}
		};

		if (isV2PagesContextMenuOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}
	}, [isV2PagesContextMenuOpen]);

	useEffect(() => {
		getUserPrefs();
		// console.log('Is API Down: ', isAPID);
		
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoggedIn])

	// Update menu when user preferences is updated
	useEffect(() => {
		if (userPreferences != null && userPreferences.menuThemeColor !== "" && userPreferences.menuThemeColor !== null && userPreferences.menuThemeColor !== undefined) {
			// console.log('Setting menu theme color: ', userPreferences.menuThemeColor);
			
			// NOTE: Disabled theme setting for Lostine Drone Co
			// setMenuThemeColor(rgbaStringToRgbaColor(userPreferences.menuThemeColor));
		}
	}, [userPreferences])

	async function getUserPrefs() {
		if (isLoggedIn) {
			// Get user preferences and set them
			const apiData: IUserPreferencesResponse = await APIGetUserPreferencesByUserUuid();
			setUserPreferences(apiData?.data);
			dispatch(setUserSlicePreferences(apiData?.[0]));

			// Store user settings into redux
			let userSettingsResponse = await APIGetUserSettings();
			let userSettings: IUserSettings = userSettingsResponse?.response[0];		

			if (userSettings !== null && userSettings !== undefined &&
				userSettings?.hardinessZone !== null && userSettings?.location !== null && userSettings?.temperatureUnit !== null) {
				dispatch(setHardinessZone(userSettings.hardinessZone));
				dispatch(setLocation(userSettings.location));
				dispatch(setTemperatureUnit(userSettings.temperatureUnit));
			}

			// Store weather data into redux
			if (userSettings !== null && userSettings?.location != null && userSettings?.location !== null) {
				// console.log("Location: ", userSettings.location);
				
				// console.log("Location reformatted: ", userSettings.location.split(',')[0].trim());
				
				APIGetGeoLocation(userSettings.location.split(',')[0].trim()).then((data) => {
					console.log(data.response[0]);
					APIGetWeatherData(data?.response[0].lat, data.response[0].lon).then((data) => {
						dispatch(setWeatherData(data?.response));
						dispatch(setCurrentTemp(data?.response?.current?.temp));
					});
				}); 
			}
		}
	}

	// Idle timer
    const [idleState, setIdleState] = useState<string>('Active');
    const [idleCounter, setIdleCounter] = useState<number>(0);
    const [remainingTime, setRemainingTime] = useState<number>(0);
    const onIdle = () => { 
        setIdleState('Idle');
		if (userPreferences?.idleTimeoutEnabled && isLoggedIn) {
			console.log('Logging out due to idle timeout...');
			handleLogout();
		}
    };
    const onActive = () => { setIdleState('Active'); };
    const onAction = (prevState) => { setIdleCounter(prevState + 1); };

    const { getRemainingTime } = useIdleTimer({
        onIdle,
        onActive,
        onAction,
        timeout: 900000,  // 60 seconds * 15 minutes * 1000 milliseconds = 300000 milliseconds
        throttle: 500
    })
    
    useEffect(() => {
        const interval = setInterval(() => {
        	setRemainingTime(Math.ceil(getRemainingTime() / 1000))
        }, 500)
    
        return () => {
        	clearInterval(interval)
        }
    }, [])
    // End idle timer

	const checkIfLoggedIn = () => {
		if ((typeof JSON.parse(localStorage.getItem('user'))?.uuid) == 'string') {
			// TODO: Verify user credentials match server. If not, remove the local credentials
			dispatch(setLoggedIn(true));
			if (window.location.pathname === '/login' || window.location.pathname === '/register') {
				navigate('/about');
			}			
		} else {
			dispatch(setLoggedIn(false));
			localStorage.removeItem('user');
			// Remove theme classes when user is not logged in
			document.body.classList.remove('theme-light', 'theme-dark');
			// console.log("User is not logged in. Navigating home.");
			// Check if current route is in _settings_non_private_routes. If not, redirect to login
			if (!_settings_non_private_routes.includes(window.location.pathname)) {
				navigate('/');
			}
		}
	}

	// Check once on initial load if user is logged in. If not, redirect them
	useEffect(() => {
		checkIfLoggedIn();
	}, []);

	// Periodically check if user is logged in. If not, redirect them
	useEffect(() => {
		const interval = setInterval(() => {			
			checkIfLoggedIn();
		}, 300000);  // 5 * 60 * 1000 = 300000 milliseconds = 5 minutes
		return () => {
			clearInterval(interval);
		};
    }, []);

	function handleLogin() {
		setIsMenuOpen(false);
		navigate('/login');
	}

	function handleLogout() {
		setIsMenuOpen(false);
		localStorage.removeItem('user');
		// Remove theme classes when user logs out
		document.body.classList.remove('theme-light', 'theme-dark');
		dispatch(setLoggedIn(false));
		navigate('/login');
	}

	function handleAbout() {
		setIsMenuOpen(false);
		navigate('/about');
	}

	function handleAboutV2() {
		setIsMenuOpen(false);
		navigate('/about');
	}

	function handleServicesV2() {
		setIsMenuOpen(false);
		navigate('/services');
	}

	function handleAdmin() {
		setIsMenuOpen(false);
		navigate('/admin');
	}

        function handleSettings() {
                setIsMenuOpen(false);
                navigate('/settings');
        }

        function handleThemeManager() {
                setIsMenuOpen(false);
                navigate('/theme-manager');
        }

		function handleExperimental() {
			setIsMenuOpen(false);
			navigate('/experimental');
		}
	
	// function handleCreateAccount() {
	// 	setIsMenuOpen(false);
	// 	navigate('/register');
	// }

	function handleServices() {
		setIsMenuOpen(false);
		navigate('/services');
	}

	// function handleBilling() {
	// 	setIsMenuOpen(false);
	// 	window.location.href = _settings_customer.LIVE_BILLING_URL;
	// }

	function handleAnimals() {
		setIsMenuOpen(false);
		navigate('/animals');
	}

	function handleCarMaintenance() {
		setIsMenuOpen(false);
		navigate('/car-maintenance');
	}

	function handleCooking() {
		setIsMenuOpen(false);
		navigate('/cooking');
	}

	function handleEducation() {
		setIsMenuOpen(false);
		navigate('/education');
	}

	function handleGardening() {
		setIsMenuOpen(false);
		navigate('/gardening');
	}

	function handleHobbies() {
		setIsMenuOpen(false);
		navigate('/hobbies');
	}

	function handleExplore() {
		setIsMenuOpen(false);
		navigate('/explore');
	}

	function handleMaintenance() {
		setIsMenuOpen(false);
		navigate('/maintenance');
	}

	function handleNews() {
		setIsMenuOpen(false);
		navigate('/news');
	}

	function handleNotes() {
		setIsMenuOpen(false);
		navigate('/notes');
	}


	function handleWeather() {
		setIsMenuOpen(false);
		navigate('/weather');
	}

	function handleDashboard() {
		setIsMenuOpen(false);
		navigate('/dashboard');
	}

	function handlePricing() {
		setIsMenuOpen(false);
		navigate('/pricing');
	}

	function handleQuestionsAndAnswers() {
		setIsMenuOpen(false);
		navigate('/q&a');
	}

	function handleHome() {
		setIsMenuOpen(false);
		navigate('/');
	}

	return (
		<div className={`app-menu ${isMenuOpen ? "menu-open" : ""}`} style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}>
			{/* <h3>Menu</h3> */}
			<MenuButton text="Home" onClick={handleHome} style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}} icon={<i className="fas fa-home"></i>} />
			<MenuButton 
				text="Services" 
				onClick={handleServices}
				style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
			/>
			<MenuButton 
				text="About" 
				onClick={handleAbout}
				style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
			/>
			{isLoggedIn ? (
				<>
					{/* <div className="menu-divider" /> */}
					<div className="menu-divider-vertical" />
					{/* TODO: Add authentication for dashboard to only allow Brenton */}
					<MenuButton 
						text="Dashboard" 
						onClick={handleDashboard}
						style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
					/>
					{hasClearanceForAdmin && (
						<MenuButton
							text={
								<span className="menu-item-with-badge">
									<span>Experimental</span>
									<span className="menu-item-badge dev">Dev</span>
								</span>
							}
							onClick={handleExperimental}
							style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
						/>
					)}
					<div style={{ position: 'relative' }}>
						<MenuButton 
							text="Pages"
							onClick={() => setIsV2PagesContextMenuOpen(!isV2PagesContextMenuOpen)}
							style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
						/>
						{isV2PagesContextMenuOpen && (
							<div
								ref={contextMenuRef}
								className="v2-pages-context-menu"
							>
								<div
									className="context-menu-item"
									onClick={() => {
										handleAboutV2();
										setIsV2PagesContextMenuOpen(false);
								}}
							>
								About
							</div>
							<div
								className="context-menu-item"
								onClick={() => {
									handleServicesV2();
									setIsV2PagesContextMenuOpen(false);
								}}
							>
								Services
							</div>
						</div>
					)}
				</div>
				{/* <MenuButton 
						text="News" 
						onClick={handleNews}
						style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
					/> */}
					{/* <MenuButton 
						text="Weather" 
						onClick={handleWeather}
						style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
					/> */}
					{/* <div className="menu-divider" /> */}
					{/* <MenuButton 
						text="Common Q&A" 
						onClick={handleQuestionsAndAnswers}
						style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
					/> */}
					{/* <div className="two-item-menu-line">
						<MenuButton 
							text="Pricing" 
							onClick={handlePricing} 
							style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
						/>
						<MenuButton 
							text="Billing" 
							onClick={handleBilling}
							style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
						/>
					</div>
					{hasClearanceForAdmin && (
						<MenuButton 
							text="Admin" 
							onClick={handleAdmin}
							style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
						/>
					)} */}
                                        <MenuButton
                                                text="Settings"
                                                onClick={handleSettings}
                                                style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
                                        />
                                        <MenuButton
                                                text="Theme Manager"
                                                onClick={handleThemeManager}
                                                style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
                                        />
                                        <MenuButton
                                                text="Logout"
                                                onClick={handleLogout}
                                                style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}}
                                        />
					{/* {rgbaColorToRgbaString(menuThemeColor) !== '' && typeof menuThemeColor !== 'undefined' ? (
						<ColorPickerMenuApp menuThemeColor={menuThemeColor} setMenuThemeColor={setMenuThemeColor}/>
					) : (
						<ColorPickerMenuApp menuThemeColor={rgbaStringToRgbaColor('rgba(82, 82, 82, 1)')} setMenuThemeColor={setMenuThemeColor}/>
					)} */}
				</>
			) : (
				<>
					<MenuButton text="Login" onClick={handleLogin} style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}} />
					{/* <MenuButton text="Create Account" onClick={handleCreateAccount} style={{backgroundColor: rgbaColorToRgbaString(menuThemeColor)}} /> */}
				</>
			)}
			{/* <div className="app-version">
				<span>Version: {process.env.REACT_APP_VERSION}</span>
			</div> */}
		</div>
	);
};
